import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  ApiBacktestResponse,
  ApiClient,
  ApiFee,
  ApiFundOfFundsPortfolio,
  ApiModelPortfolio,
  ApiModelPortfolioApiPaginationResult,
  ApiModelPortfolioUsage,
  ApiMutualFundAssetRead,
  ApiPortfolioStatus,
  ApiPortfolioType,
  ApiProjectionResponse,
  ApiRebalancingFrequency,
  ApiRebalancingType,
  ApiSavedPortfolio,
  ApiSegregatedPortfolioAssetRead,
  ApiStrategyTemplate,
  Factor,
  UserInformation
} from 'api/rtk-api'
import { addYears } from 'date-fns'
import { set } from 'lodash'
import { all, call, fork, put, PutEffect, select, takeEvery, takeLatest } from 'redux-saga/effects'
import { RootState } from 'state/store'

import api from '@/api/_legacy'
import { defaultQueryParams, QueryParams } from '@/api/_legacy/types'
import { createLogoutExtraReducers } from '@/utilities/redux'

import { TENANT_ID } from '../../tradebuilder-v2/constants'
import { extractAssetPerformance } from '../../tradebuilder-v2/modules/03-portfolio/mappers/extract-asset-performance'
import { AssetResult } from '../../tradebuilder-v2/modules/03-portfolio/state/portfolio-slice'
import { AssetSummaryItem } from '../../tradebuilder-v2/modules/03-portfolio/types'
import {
  equalWeights,
  Errors,
  extractErrorMessagesFromServerResponseToArray,
  getRecordFromFactor,
  scaleWeights
} from '../../tradebuilder-v2/util'
import { mapToNewPortfolio } from './mappers'
import { isBalancedWeightTotal, sumWeights } from './portfolio-weight'
import { withResolvedPortfolioOwner } from './resolve-portfolio-owner'
import { validate } from './validation-rules'

export type SearchAssetState = {
  assetBeingInspected?: ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead
  assets: AssetResult[]
  filteredAssets: AssetResult[]
  searchTerm: string
  status: 'finding' | 'inspecting'
}

export type PortfolioSearchFilter = {
  searchTerm: string
  showFofPortfolios: boolean
  showModelPortfolios: boolean
  showOnlyActivePortfolios: boolean
  showOnlyInactivePortfolios: boolean
}

export type EditPortfoliosState = {
  allAssetPerformance?: Record<string, AssetSummaryItem[]>
  allClients: ApiClient[]
  approvalByPM: boolean
  approvalStatus: 'Approved' | 'Rejected' | ''
  approvalSuccess: boolean
  approvalUserName: string
  assetBeingInspected?: ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead
  assetDisallowedStrategies: string[]
  availableCptyIds: string[]
  availableStrategies: ApiStrategyTemplate[]
  canDisplayCharts: boolean
  canProceed: boolean
  canProceedApproval: boolean
  currentPageNumber: number
  errors: Errors
  filter: PortfolioSearchFilter
  isChoosingAsset: boolean
  isChosenAsset: boolean
  isErrorGeneratingBacktest: boolean
  isErrorGeneratingProjection: boolean
  isGeneratingBacktest: boolean
  isGeneratingProjection: boolean
  isInspecting: boolean
  isInspectingAsset: boolean
  isNew: boolean
  numTotalPortfolios: number
  performanceMetrics?: Record<string, number>
  portfolios: ApiSavedPortfolio[]
  projection?: Record<string, number>[]
  proposedPortfolio?: ApiSavedPortfolio
  query: QueryParams
  saveSuccess: boolean
  searchAsset: SearchAssetState
  selectedAssetDuration?: string
  selectedAssetPerformance?: AssetSummaryItem[]
  selectedPortfolio?: ApiSavedPortfolio
  selectedPortfolioUsage?: ApiModelPortfolioUsage
  serverErrors?: string[]
  totalInvestmentWeights: number
  totalWeights: number
  user?: UserInformation
}

const initialState: EditPortfoliosState = {
  assetDisallowedStrategies: [],
  approvalStatus: '',
  approvalSuccess: false,
  approvalUserName: '',
  approvalByPM: false,
  canProceed: false,
  canProceedApproval: false,
  saveSuccess: false,
  numTotalPortfolios: 0,
  currentPageNumber: 1,
  portfolios: [],
  availableStrategies: [],
  filter: {
    searchTerm: '',
    showModelPortfolios: true,
    showFofPortfolios: true,
    showOnlyActivePortfolios: false,
    showOnlyInactivePortfolios: false
  },
  isInspectingAsset: false,
  isNew: false,
  isInspecting: false,
  totalWeights: 0,
  totalInvestmentWeights: 0,
  allClients: [],
  availableCptyIds: [],
  searchAsset: {
    status: 'finding',
    searchTerm: '',
    assets: [],
    filteredAssets: []
  },
  isChoosingAsset: false,
  isChosenAsset: false,
  errors: {},
  isErrorGeneratingBacktest: false,
  isErrorGeneratingProjection: false,
  isGeneratingBacktest: false,
  isGeneratingProjection: false,
  canDisplayCharts: false,
  query: { ...defaultQueryParams }
}

function getTaxClassification(portfolio: ApiSavedPortfolio, totalWeights: number) {
  const yieldWeight = portfolio.targetWeights
    .filter(x => x.asset.taxClassification === 'Yield')
    .map(x => x.weight)
    .reduce((sum, x) => sum + x, 0)
  const taxClassification = yieldWeight >= 0.5 * totalWeights ? 'Yield' : 'Capital'
  return taxClassification
}

function updateWeightValidation(state: EditPortfoliosState, totalWeight: number) {
  if (!isBalancedWeightTotal(totalWeight)) {
    state.errors.total_weights = 'Weights must sum to 100%'
    state.canProceed = false
  } else {
    state.canProceed = true
  }
}

function isFundOfFundsPortfolio(
  portfolio: ApiSavedPortfolio
): portfolio is ApiSavedPortfolio & ApiFundOfFundsPortfolio {
  return portfolio.type === 'FundOfFunds'
}

function updateFundOfFundsInvestmentWeight(
  portfolio: ApiSavedPortfolio,
  assetId: string,
  weight: number
) {
  if (!isFundOfFundsPortfolio(portfolio)) return

  if (!portfolio.investmentWeights) {
    portfolio.investmentWeights = []
  }

  const i = portfolio.investmentWeights.findIndex(x => x.assetId === assetId)
  if (i === -1) {
    portfolio.investmentWeights.push({
      assetId,
      weight
    })
  } else {
    portfolio.investmentWeights[i].weight = weight
  }
}

function removeFundOfFundsInvestmentWeight(portfolio: ApiSavedPortfolio, assetId: string) {
  if (!isFundOfFundsPortfolio(portfolio) || !portfolio.investmentWeights) return

  portfolio.investmentWeights = portfolio.investmentWeights.filter(x => x.assetId !== assetId)
}

function syncFundOfFundsInvestmentWeights(portfolio: ApiSavedPortfolio) {
  if (!isFundOfFundsPortfolio(portfolio)) return

  portfolio.investmentWeights = portfolio.targetWeights.map(x => ({
    assetId: x.asset.assetId,
    weight: x.weight
  }))
}

function getAssetDisallowedStrategies(
  assets: (ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead)[]
) {
  const disallowedStrategies = []
  assets.forEach(asset => {
    asset.disallowedStrategies.forEach(strat => {
      if (!disallowedStrategies.includes(strat)) {
        disallowedStrategies.push(strat)
      }
    })
  })
  return disallowedStrategies
}

export const editPortfoliosSlice = createSlice({
  name: 'editPortfolios',
  initialState,
  reducers: {
    init: () => {
      // noop
    },
    chooseAsset: state => {
      state.isChoosingAsset = true
    },
    saveError: (state, action: PayloadAction<any>) => {
      state.serverErrors = extractErrorMessagesFromServerResponseToArray(action.payload)
    },
    fetchPortfolioDetailSuccess: (
      state,
      action: PayloadAction<ApiModelPortfolioApiPaginationResult>
    ) => {
      state.numTotalPortfolios = action.payload.totalDocuments
      state.portfolios = action.payload.data as unknown as ApiSavedPortfolio[]
    },
    fetchAssetDetailSuccess: (
      state,
      action: PayloadAction<ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead>
    ) => {
      state.searchAsset.assetBeingInspected = action.payload
    },
    fetchStrategiesSuccess: (state, action: PayloadAction<ApiStrategyTemplate[]>) => {
      state.availableStrategies = action.payload
    },
    fetchClientsDetailSuccess: (state, action: PayloadAction<UserInformation>) => {
      state.allClients = action.payload.clients
      state.user = action.payload
    },
    fetchCptyIdsSuccess: (state, action: PayloadAction<string[]>) => {
      state.availableCptyIds = action.payload
    },
    fetchAssetsSuccess: (
      state,
      action: PayloadAction<(ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead)[]>
    ) => {
      const allAssets = action.payload.map(x => ({
        id: x.assetId,
        name: x.name,
        assetType: x.type,
        protectionType: x.protectionClassification,
        taxClassification: x.taxClassification,
        assetClass: x.assetClass,
        isMarketDataReady: x.isMarketDataReady
      }))
      const assetIdsInPortfolio = state.selectedPortfolio.targetWeights.map(x => x.asset.assetId)
      state.searchAsset.assets = allAssets
        .filter(x => !assetIdsInPortfolio.includes(x.id))
        .filter(x => x.protectionType !== 'Protection')
      state.searchAsset.filteredAssets = [...state.searchAsset.assets]
    },
    search: (state, action: PayloadAction<PortfolioSearchFilter>) => {
      const filter = action.payload
      if (filter.searchTerm) {
        state.query.WildcardString = filter.searchTerm
      } else {
        state.query.WildcardString = undefined
      }

      if (!filter.showModelPortfolios && !filter.showFofPortfolios) {
        // not allowed to unselect both
        if (!state.filter.showModelPortfolios) {
          filter.showModelPortfolios = true
        }
        if (!state.filter.showFofPortfolios) {
          filter.showFofPortfolios = true
        }
      }

      state.query.PortfolioTypes = []
      if (filter.showModelPortfolios) {
        state.query.PortfolioTypes.push('ModelPortfolio')
      }
      if (filter.showFofPortfolios) {
        state.query.PortfolioTypes.push('FundOfFunds')
      }
      if (state.query.PortfolioTypes.length === 0) {
        state.query.PortfolioTypes = undefined
      }

      if (filter.showOnlyActivePortfolios) {
        state.query.PortfolioStatusFilter = 'Active'
      } else if (!filter.showOnlyActivePortfolios && filter.showOnlyInactivePortfolios) {
        state.query.PortfolioStatusFilter = 'Inactive'
      } else {
        state.query.PortfolioStatusFilter = 'All'
      }

      state.filter = filter
    },
    filter: (state, action: PayloadAction<string[]>) => {
      state.query.Status = action.payload.join(',')
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.query.PageNumber = action.payload
    },
    clearFilters: state => {
      state.query.Status = ''
      state.query.WildcardString = ''
    },
    setInvalid: (state, action: PayloadAction<Errors>) => {
      state.errors = action.payload
    },
    setSaveSuccessFalse: state => {
      state.saveSuccess = false
    },
    setDataIncomplete: state => {
      state.canProceed = false
      state.canDisplayCharts = false
    },
    setDataComplete: state => {
      state.canDisplayCharts = true
    },
    savePortfolio: state => {
      state.serverErrors = null
    },
    savePortfolioSuccess: state => {
      state.canProceed = false
      state.isNew = false
      state.saveSuccess = true
    },
    stopInspecting: state => {
      state.isInspecting = false
      state.performanceMetrics = null
      state.projection = null
      state.canProceed = false
      state.canDisplayCharts = false
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchSelectedPortfolio: (state, action: PayloadAction<string>) => {
      // handled by saga
    },
    setIsNew: state => {
      state.isNew = true
      state.saveSuccess = false
      state.approvalSuccess = false
      state.isInspecting = true
      state.totalWeights = 0
      state.selectedAssetPerformance = []
      state.errors = {}
      state.selectedPortfolioUsage = null
      state.selectedPortfolio = {
        type: 'None',
        portfolioId: '',
        portfolioName: '',
        fees: [],
        rebalancingDefinition: {
          type: 'None'
        },
        targetWeights: [],
        disallowedStrategies: [],
        allowedBusinessUnitIds: []
      }
      state.proposedPortfolio = {
        type: 'None',
        portfolioId: '',
        portfolioName: '',
        fees: [],
        rebalancingDefinition: {
          type: 'None'
        },
        targetWeights: [],
        disallowedStrategies: [],
        allowedBusinessUnitIds: []
      }
    },
    setPortfolioType: (state, action: PayloadAction<ApiPortfolioType>) => {
      state.selectedPortfolio.type = action.payload
    },
    setSelectedAssetDuration: (state, action: PayloadAction<string>) => {
      state.selectedAssetDuration = action.payload
      state.selectedAssetPerformance = state.allAssetPerformance[state.selectedAssetDuration]
    },
    inspectAsset: (state, action: PayloadAction<string>) => {
      state.isInspectingAsset = true
      const asset = state.selectedPortfolio.targetWeights
        .flatMap(x => x.asset)
        .find(x => x.assetId === action.payload)
      state.assetBeingInspected = asset as unknown as
        | ApiMutualFundAssetRead
        | ApiSegregatedPortfolioAssetRead
    },
    stopInspectingAsset: state => {
      state.isInspectingAsset = false
      state.assetBeingInspected = null
    },
    changeAssetSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchAsset.searchTerm = action.payload
      if (action.payload === '') {
        state.searchAsset.filteredAssets = [...state.searchAsset.assets]
        return
      }
      state.searchAsset.filteredAssets = state.searchAsset.assets?.filter(
        x => x.name?.toLowerCase().indexOf(action.payload.toLowerCase()) !== -1
      )
    },
    setPortfolioName: (state, action: PayloadAction<string>) => {
      state.selectedPortfolio.portfolioName = action.payload
      delete state.errors.portfolio_name
      state.canProceed = true
      state.saveSuccess = false
    },
    setPortfolioOwnerId: (state, action: PayloadAction<string>) => {
      ;(state.selectedPortfolio as any).portfolioOwnerId = action.payload
    },
    setPortfolioId: (state, action: PayloadAction<string>) => {
      state.selectedPortfolio.portfolioId = action.payload
      delete state.errors.id
      state.canProceed = true
      state.saveSuccess = false
    },
    setRebalType: (state, action: PayloadAction<ApiRebalancingType>) => {
      state.selectedPortfolio.rebalancingDefinition.type = action.payload
      state.canProceed = true
      state.saveSuccess = false
    },
    setRebalFreq: (state, action: PayloadAction<ApiRebalancingFrequency>) => {
      state.selectedPortfolio.rebalancingDefinition.frequency = action.payload
      state.canProceed = true
      state.saveSuccess = false
    },
    setRebalTolerance: (state, action: PayloadAction<number>) => {
      state.selectedPortfolio.rebalancingDefinition.weightsTolerance = action.payload
      state.canProceed = true
      state.saveSuccess = false
    },
    setMultiplier: (state, action: PayloadAction<number>) => {
      state.selectedPortfolio.multiplier = action.payload
      state.canProceed = true
      state.saveSuccess = false
    },
    setStrategyPermission: (state, action: PayloadAction<{ id: string; val: boolean }>) => {
      const { id } = action.payload
      const { val } = action.payload
      const isAllowed = !state.selectedPortfolio.disallowedStrategies.includes(id)
      if (isAllowed && !val) {
        state.selectedPortfolio.disallowedStrategies.push(id)
      }
      if (!isAllowed && val) {
        const index = state.selectedPortfolio.disallowedStrategies.indexOf(id)
        state.selectedPortfolio.disallowedStrategies.splice(index, 1)
      }
      state.canProceed = true
      state.saveSuccess = false
    },
    setBusinessUnitPermissions: (state, action: PayloadAction<{ ids: string[]; val: boolean }>) => {
      const { val } = action.payload
      const allowedBuIds = !state.selectedPortfolio.allowedBusinessUnitIds
        ? []
        : state.selectedPortfolio.allowedBusinessUnitIds
      action.payload.ids.forEach(id => {
        const isAllowed = allowedBuIds.includes(id)
        if (!isAllowed && val) {
          allowedBuIds.push(id)
        }
        if (isAllowed && !val) {
          const index = allowedBuIds.indexOf(id)
          allowedBuIds.splice(index, 1)
        }
      })
      state.selectedPortfolio.allowedBusinessUnitIds = allowedBuIds
      state.canProceed = true
    },
    changeAssetWeight: (state, action: PayloadAction<{ assetId: string; weight: number }>) => {
      const i = state.selectedPortfolio.targetWeights.findIndex(
        x => x.asset.assetId === action.payload.assetId
      )
      state.selectedPortfolio.targetWeights[i].weight = action.payload.weight
      updateFundOfFundsInvestmentWeight(
        state.selectedPortfolio,
        action.payload.assetId,
        action.payload.weight
      )

      delete state.errors.total_weights
      const weights = state.selectedPortfolio.targetWeights.map(x => x.weight)
      state.totalWeights = sumWeights(weights)
      updateWeightValidation(state, state.totalWeights)

      state.selectedPortfolio.taxClassification = getTaxClassification(
        state.selectedPortfolio,
        state.totalWeights
      )
    },
    removeAsset: (state, action: PayloadAction<string>) => {
      state.selectedPortfolio.targetWeights = state.selectedPortfolio.targetWeights.filter(
        x => x.asset.assetId !== action.payload
      )
      removeFundOfFundsInvestmentWeight(state.selectedPortfolio, action.payload)

      delete state.errors.total_weights
      const weights = state.selectedPortfolio.targetWeights.map(x => x.weight)
      state.totalWeights = sumWeights(weights)
      updateWeightValidation(state, state.totalWeights)

      state.selectedPortfolio.taxClassification = getTaxClassification(
        state.selectedPortfolio,
        state.totalWeights
      )

      state.assetDisallowedStrategies = getAssetDisallowedStrategies(
        state.selectedPortfolio.targetWeights.map(x => x.asset) as any
      )

      if (state.selectedPortfolio.targetWeights.length === 0) {
        state.isChosenAsset = false
      }
    },
    addAsset: (
      state,
      action: PayloadAction<ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead>
    ) => {
      const asset = action.payload
      const targetWeight = {
        asset,
        weight: null
      }
      const assetPerformance = {
        id: asset.assetId,
        name: asset.name,
        return: null,
        volatility: null,
        weight: null
      }
      state.selectedPortfolio.targetWeights.push(targetWeight)
      state.selectedAssetPerformance.push(assetPerformance)

      state.assetDisallowedStrategies = getAssetDisallowedStrategies(
        state.selectedPortfolio.targetWeights.map(x => x.asset) as any
      )

      state.isChosenAsset = true
      state.isChoosingAsset = false
      state.searchAsset.status = 'finding'
    },
    equaliseAssetWeights: state => {
      const numAssets = state.selectedPortfolio.targetWeights.length
      const weightsArr = equalWeights(numAssets, 4)
      state.selectedPortfolio.targetWeights.map((x, i) => (x.weight = weightsArr[i]))
      syncFundOfFundsInvestmentWeights(state.selectedPortfolio)

      delete state.errors.total_weights
      const weights = state.selectedPortfolio.targetWeights.map(x => x.weight)
      state.totalWeights = sumWeights(weights)
      updateWeightValidation(state, state.totalWeights)

      state.selectedPortfolio.taxClassification = getTaxClassification(
        state.selectedPortfolio,
        state.totalWeights
      )
    },
    scaleAssetWeights: state => {
      const weightsArr = scaleWeights(
        state.selectedPortfolio.targetWeights.map(x => x.weight),
        4
      )
      state.selectedPortfolio.targetWeights.map((x, i) => (x.weight = weightsArr[i]))
      syncFundOfFundsInvestmentWeights(state.selectedPortfolio)

      delete state.errors.total_weights
      const weights = state.selectedPortfolio.targetWeights.map(x => x.weight)
      state.totalWeights = sumWeights(weights)
      updateWeightValidation(state, state.totalWeights)

      state.selectedPortfolio.taxClassification = getTaxClassification(
        state.selectedPortfolio,
        state.totalWeights
      )
    },
    stopChoosingAsset: state => {
      state.isChoosingAsset = false
      state.searchAsset.status = 'finding'
    },
    goBackToAssetSearch: state => {
      state.searchAsset.status = 'finding'
    },
    resetDisallowedStrategies: state => {
      state.selectedPortfolio.disallowedStrategies = state.assetDisallowedStrategies
    },
    fetchSelectedPortfolioSuccess: (
      state,
      action: PayloadAction<{
        portfolio: ApiModelPortfolio | ApiFundOfFundsPortfolio
        portfolioUsage: ApiModelPortfolioUsage
      }>
    ) => {
      state.selectedPortfolio = action.payload.portfolio as unknown as ApiSavedPortfolio

      state.assetDisallowedStrategies = getAssetDisallowedStrategies(
        state.selectedPortfolio.targetWeights.map(x => x.asset) as any
      )

      if (action.payload.portfolio?.pendingChange?.auditedPortfolioData) {
        state.proposedPortfolio = {
          ...state.selectedPortfolio,
          ...action.payload.portfolio.pendingChange.auditedPortfolioData,
          pendingChange: undefined
        }
      }

      state.selectedPortfolioUsage = action.payload.portfolioUsage
      state.isNew = false
      state.saveSuccess = false
      state.isInspecting = true
      state.selectedAssetPerformance = []
      state.totalWeights = 1
      state.errors = {}
    },
    fetchSelectedPortfolioFailure: (state, action: PayloadAction<any>) => {
      state.serverErrors = extractErrorMessagesFromServerResponseToArray(action.payload)
    },
    fetchingPortfolioBacktest: state => {
      state.isErrorGeneratingBacktest = false
      state.isGeneratingBacktest = true
    },
    fetchingPortfolioProjection: state => {
      state.isErrorGeneratingProjection = false
      state.isGeneratingProjection = true
    },
    fetchPortfolioBacktestSuccess: (state, action: PayloadAction<ApiBacktestResponse>) => {
      state.performanceMetrics = getRecordFromFactor(action.payload.backtestTimeseries)
      state.allAssetPerformance = extractAssetPerformance(
        state.selectedPortfolio.targetWeights,
        action.payload.assetPerformance
      )
      state.selectedAssetDuration = '6m'
      state.selectedAssetPerformance = state.allAssetPerformance[state.selectedAssetDuration]
      state.isGeneratingBacktest = false
    },
    fetchPortfolioProjectionSuccess: (state, action: PayloadAction<ApiProjectionResponse>) => {
      state.projection = Object.values(action.payload).filter(x => x) as any
      state.isGeneratingProjection = false
    },
    fetchPortfolioBacktestFailure: state => {
      state.isGeneratingBacktest = false
      state.isErrorGeneratingBacktest = true
    },
    fetchPortfolioProjectionFailure: state => {
      state.isGeneratingProjection = false
      state.isErrorGeneratingProjection = true
    },
    addNewFee: (state, action: PayloadAction<ApiFee>) => {
      state.selectedPortfolio.fees.push(action.payload)
      state.canProceed = true
    },
    removeFee: (state, action: PayloadAction<number>) => {
      state.selectedPortfolio.fees.splice(action.payload, 1)
      state.canProceed = true
    },
    changeFeeProperty: (
      state,
      action: PayloadAction<{ index: number; property: string; updatedValue: any }>
    ) => {
      set(
        state.selectedPortfolio.fees[action.payload.index],
        action.payload.property,
        action.payload.updatedValue
      )
      state.canProceed = true
      delete state.errors.feeRates
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    approvePortfolio: state => {
      // handled by saga
    },
    setApprovalSuccess: (state, action: PayloadAction<boolean>) => {
      state.approvalSuccess = action.payload
    },
    setApprovalStatus: (state, action: PayloadAction<'Approved' | 'Rejected' | ''>) => {
      state.approvalStatus = action.payload
      if (state.approvalStatus !== '' && state.approvalUserName !== '') {
        state.canProceedApproval = true
      } else {
        state.canProceedApproval = false
      }
    },
    setApprovalUserName: (state, action: PayloadAction<string>) => {
      state.approvalUserName = action.payload
      if (state.approvalStatus !== '' && state.approvalUserName !== '') {
        state.canProceedApproval = true
      } else {
        state.canProceedApproval = false
      }
    },
    setPortfolioStatus: (state, action: PayloadAction<ApiPortfolioStatus>) => {
      if (state.selectedPortfolio) {
        state.selectedPortfolio = {
          ...state.selectedPortfolio,
          status: action.payload
        }
      }
    },
    resetApprovalStatus: state => {
      state.canProceedApproval = false
      state.approvalStatus = ''
      state.approvalUserName = ''
    },
    setPortfolioManagerChecked: (state, action: PayloadAction<boolean>) => {
      state.approvalByPM = action.payload
      delete state.errors.portfolio_name
      state.canProceed = true
    },
    setProposedToSelected: state => {
      state.selectedPortfolio = state.proposedPortfolio
    }
  },
  extraReducers: builder => {
    builder.addCase('editPortfolios/inspectAssetForSelection', state => {
      state.searchAsset.status = 'inspecting'
    })
    // Apply logout extra reducers
    const logoutReducers = createLogoutExtraReducers(initialState)
    Object.entries(logoutReducers).forEach(([actionType, reducer]) => {
      builder.addCase(actionType as any, reducer as any)
    })
  }
})

export const actions = {
  ...editPortfoliosSlice.actions,
  inspectAssetForSelection: (id: string) => ({
    type: 'editPortfolios/inspectAssetForSelection',
    payload: id
  })
}
export type EditPortfoliosActions = typeof actions

function* fetchStrategyTemplatesSaga() {
  try {
    const response = yield call(api.tenant.strategyDetail, TENANT_ID)
    yield put({
      type: 'editPortfolios/fetchStrategiesSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'editPortfolios/fetchStrategiesFailure', payload: error })
  }
}

function* fetchPortfolioDetailSaga() {
  const rootState: RootState = yield select()
  const state = rootState.editPortfolios
  const { query } = state

  try {
    const response = yield call(api.tenant.listDetail3, TENANT_ID, query)
    yield put({
      type: 'editPortfolios/fetchPortfolioDetailSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'editPortfolios/fetchPortfolioDetailFailure', payload: error })
  }
}

function* fetchClientsDetailSaga() {
  try {
    const response = yield call(api.usermanager.currentList)
    yield put({
      type: 'editPortfolios/fetchClientsDetailSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'editPortfolios/fetchClientsDetailFailure', payload: error })
  }
}

function* fetchCptyIdsSaga() {
  try {
    const response = yield call(api.tenant.counterpartyidsDetail, TENANT_ID)
    yield put({
      type: 'editPortfolios/fetchCptyIdsSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'editPortfolios/fetchCptyIdsFailure', payload: error })
  }
}

function* fetchInitialDataSaga() {
  yield all([
    fetchPortfolioDetailSaga(),
    fetchStrategyTemplatesSaga(),
    fetchClientsDetailSaga(),
    fetchCptyIdsSaga()
  ])
}

function* fetchSelectedPortfolioSaga(action: PayloadAction<string>) {
  try {
    const rootState: RootState = yield select()
    const state = rootState.editPortfolios
    const selectedPortfolio = state.portfolios.find(x => x.portfolioId === action.payload)
    const response = yield call(api.tenant.usageDetail, selectedPortfolio.portfolioId, TENANT_ID)
    const result = { portfolio: selectedPortfolio, portfolioUsage: response.data }
    yield put({ type: 'editPortfolios/fetchSelectedPortfolioSuccess', payload: result })
  } catch (error) {
    yield put({ type: 'editPortfolios/fetchSelectedPortfolioFailure', payload: error })
  }
}

function* fetchAssetsSaga() {
  try {
    const response = yield call(api.tenant.assetDetail, TENANT_ID)
    yield put({ type: 'editPortfolios/fetchAssetsSuccess', payload: response.data })
  } catch (error) {
    yield put({ type: 'editPortfolios/fetchAssetsFailure', payload: error })
  }
}

function* fetchAssetDetailSaga(action: PayloadAction<string>) {
  try {
    const response = yield call(api.tenant.assetDetail2, action.payload, TENANT_ID)
    yield put({ type: 'editPortfolios/fetchAssetDetailSuccess', payload: response.data })
  } catch (error) {
    yield put({ type: 'editPortfolios/fetchAssetDetailFailure', payload: error })
  }
}

function* fetchPortfolioPerformanceSaga(request) {
  const investment = 100

  yield put({ type: 'editPortfolios/fetchingPortfolioBacktest' })
  try {
    const result = yield call(api.tenant.portfolioBacktestCreate, TENANT_ID, request)

    const data = result.data as ApiBacktestResponse
    const multiplied: Factor[] = []
    const timeseries = result.data.backtestTimeseries
    for (const date of Object.keys(timeseries)) {
      multiplied.push({
        timestamp: date,
        value: timeseries[date] * Number(investment)
      })
    }

    data.backtestTimeseries = multiplied as any

    yield put({ type: 'editPortfolios/fetchPortfolioBacktestSuccess', payload: data })
  } catch (error) {
    yield put({ type: 'editPortfolios/fetchPortfolioBacktestFailure', payload: error })
  }
}

function* fetchPortfolioProjectionSaga(request) {
  const investment = 100

  yield put({ type: 'editPortfolios/fetchingPortfolioProjection' })
  try {
    const result = yield call(api.tenant.portfolioProjectionCreate, TENANT_ID, request)

    const data = result.data as ApiProjectionResponse
    const timeserieses = Object.values(data)
    const multiplied: Record<string, number>[] = []

    for (const timeseries of timeserieses) {
      const values: Record<string, number> = {}
      for (const date of Object.keys(timeseries)) {
        values[date] = timeseries[date] * Number(investment)
      }

      multiplied.push(values)
    }

    yield put({ type: 'editPortfolios/fetchPortfolioProjectionSuccess', payload: multiplied })
  } catch (error) {
    yield put({ type: 'editPortfolios/fetchPortfolioProjectionFailure', payload: error })
  }
}

function* reloadPerformanceAndProjectionSaga() {
  const state: RootState = yield select()
  const { editPortfolios } = state

  if (!editPortfolios.selectedPortfolio) {
    return
  }

  // validate before requesting charts
  const validationResult = validate(editPortfolios)

  if (!validationResult.isValid) {
    yield put(actions.setDataIncomplete())
    return
  }

  yield put(actions.setDataComplete())

  const portfolioInvestment = editPortfolios.selectedPortfolio.targetWeights.map(targetWeight => ({
    assetId: targetWeight.asset.assetId,
    weight: targetWeight.weight
  }))

  const backtestRequest = {
    start: null,
    end: null,
    portfolioInvestment
  }

  const projectionRequest = {
    start: new Date().toJSON(),
    end: addYears(new Date(), 5).toJSON(),
    portfolioInvestment
  }

  yield fork(fetchPortfolioPerformanceSaga, backtestRequest)
  yield fork(fetchPortfolioProjectionSaga, projectionRequest)
}

function* reloadChartsInResponseToActionsSaga(action) {
  if (
    action.type === 'editPortfolios/fetchSelectedPortfolioSuccess' ||
    action.type === 'editPortfolios/changeAssetWeight' ||
    action.type === 'editPortfolios/addAsset' ||
    action.type === 'editPortfolios/removeAsset' ||
    action.type === 'editPortfolios/setRebalType' ||
    action.type === 'editPortfolios/setRebalFreq' ||
    action.type === 'editPortfolios/setRebalTolerance' ||
    action.type === 'editPortfolios/equaliseAssetWeights' ||
    action.type === 'editPortfolios/scaleAssetWeights' ||
    action.type === 'editPortfolios/setPortfolioManagerChecked'
  ) {
    yield reloadPerformanceAndProjectionSaga()
  }
}

function savePortfolioSaga(onSuccess: PutEffect, onFail: (e: any) => PutEffect) {
  return function* saveAsset2Saga() {
    const state = yield select()
    const { editPortfolios } = state
    const { selectedPortfolio }: { selectedPortfolio: ApiSavedPortfolio } = editPortfolios

    const selectedPortfolioWithOwner = withResolvedPortfolioOwner(
      selectedPortfolio as any,
      editPortfolios.user
    ) as ApiSavedPortfolio
    const result = validate({
      ...editPortfolios,
      selectedPortfolio: selectedPortfolioWithOwner
    })
    if (!result.isValid) {
      yield put(actions.setInvalid(result.errors))
    } else {
      try {
        const portfolio = mapToNewPortfolio(selectedPortfolioWithOwner as any)
        if (editPortfolios.isNew) {
          yield call(api.tenant.portfolioCreate, TENANT_ID, portfolio)
          yield onSuccess
        } else {
          if (!(selectedPortfolioWithOwner as any).portfolioId) {
            throw new Error('FATAL ERROR: Model Portfolio Id does not exist')
          }
          yield call(api.tenant.portfolioUpdate, TENANT_ID, selectedPortfolioWithOwner as any)
          yield onSuccess
        }
      } catch (error) {
        yield onFail(error)
      }
    }
  }
}

function approvePortfolioSaga(onSuccess: PutEffect, onFail: (e: any) => PutEffect) {
  return function* approvePortfolio2Saga() {
    const state = yield select()
    const { editPortfolios } = state
    const {
      approvalStatus,
      selectedPortfolio
    }: {
      approvalStatus: 'Approved' | 'Rejected' | ''
      selectedPortfolio: ApiSavedPortfolio
    } = editPortfolios

    try {
      if (approvalStatus === 'Approved') {
        yield call(api.tenant.approveUpdate, selectedPortfolio.portfolioId, TENANT_ID)
        yield put(actions.setPortfolioStatus('Active'))
        if (selectedPortfolio.status === 'PendingApprovalEdit') {
          yield put(actions.setProposedToSelected())
        }
      } else {
        yield call(api.tenant.rejectUpdate, selectedPortfolio.portfolioId, TENANT_ID)
        yield put(actions.setPortfolioStatus('RejectedNew'))
      }
      yield onSuccess
      yield put(actions.resetApprovalStatus())
      yield put(actions.setApprovalSuccess(true))
    } catch (error) {
      yield onFail(error)
    }
  }
}

export function* editPortfolioRootSaga() {
  yield all([
    takeLatest('editPortfolios/init', fetchInitialDataSaga),
    takeLatest('editPortfolios/search', fetchPortfolioDetailSaga),
    takeLatest('editPortfolios/fetchSelectedPortfolio', fetchSelectedPortfolioSaga),
    takeLatest('editPortfolios/savePortfolioSuccess', fetchPortfolioDetailSaga),
    takeLatest('editPortfolios/chooseAsset', fetchAssetsSaga),
    takeLatest('editPortfolios/inspectAssetForSelection', fetchAssetDetailSaga),
    takeLatest(
      'editPortfolios/approvePortfolio',
      approvePortfolioSaga(put(actions.savePortfolioSuccess()), e => put(actions.saveError(e)))
    ),
    takeLatest(
      'editPortfolios/savePortfolio',
      savePortfolioSaga(put(actions.savePortfolioSuccess()), e => put(actions.saveError(e)))
    ),
    takeEvery('*', reloadChartsInResponseToActionsSaga)
  ])
}

export default editPortfoliosSlice.reducer
